import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
} from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInUserRoles } from '~/components/Auth';
import { Roles } from '~/data/user';
import {
  convertEmployeeRolesSelectionStateToRoles,
  convertRolesToEmployeeRolesSelectionState,
  resolveNewEmployeeRolesSelectionState,
} from '~/scenes/PeopleModule/components/EmployeeActions/ChangeRole/EmployeeRolesSelector/data';
import { isRoleHRAdmin, isRoleHRAdminFull } from '~/services/Permissions';

type PropsType = {
  name: string;
  value: Roles[];
  onChange: (newValue: Roles[]) => void;
  columnCount?: number;
  error?: boolean;
  helperText?: string;
};

export const EmployeeRolesSelector = (props: PropsType) => {
  const { name, value, onChange, columnCount, error, helperText } = props;

  const { t } = useTranslation();
  const roles = useLoggedInUserRoles();

  const empRoles = useMemo(() => {
    return convertRolesToEmployeeRolesSelectionState(value);
  }, [value]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(
        convertEmployeeRolesSelectionStateToRoles(
          resolveNewEmployeeRolesSelectionState(empRoles)(
            e.target.value,
            e.target.checked,
          ),
        ),
      );
    },
    [empRoles, onChange],
  );

  const RoleOption = (roleProps: {
    checked?: boolean;
    role: Roles;
    label: string;
  }) => {
    return (
      <Box>
        <FormControlLabel
          control={
            <Checkbox
              name={name}
              checked={roleProps.checked}
              value={roleProps.role}
              onChange={handleChange}
            />
          }
          label={roleProps.label}
        />
      </Box>
    );
  };

  return (
    <FormControl error={error}>
      <FormLabel>{t('laRole')}</FormLabel>
      <Box sx={{ columnCount: columnCount ?? 1 }}>
        <RoleOption
          role={Roles.EMPLOYEE}
          checked={empRoles.EMPLOYEE}
          label={t('laEmployee')}
        />
        <RoleOption
          role={Roles.IT_ADMIN}
          checked={empRoles.IT_ADMIN}
          label={t('laITAdmin')}
        />
        <RoleOption
          role={Roles.DEPUTY_MANAGER}
          checked={empRoles.DEPUTY_MANAGER}
          label={t('laDeputyManager')}
        />
        <RoleOption
          role={Roles.ALT_MANAGER}
          checked={empRoles.ALT_MANAGER}
          label={t('laAltManager')}
        />
        <RoleOption
          role={Roles.MANAGER}
          checked={empRoles.MANAGER}
          label={t('laManager')}
        />
        <RoleOption
          role={Roles.HR_DEV}
          checked={empRoles.HR_DEV}
          label={t('laHRDev')}
        />
        <RoleOption
          role={Roles.HR_STAFF}
          checked={empRoles.HR_STAFF}
          label={t('laHR')}
        />
        {isRoleHRAdminFull(roles) && (
          <RoleOption
            role={Roles.HR_ADMIN}
            checked={empRoles.HR_ADMIN}
            label={t('laHRAdmin')}
          />
        )}
        {isRoleHRAdmin(roles) && (
          <RoleOption
            role={Roles.HR_ADMIN_LIMITED}
            checked={empRoles.HR_ADMIN_LIMITED}
            label={t('laHRAdminLimited')}
          />
        )}
        <RoleOption
          role={Roles.HR_SALARY}
          checked={empRoles.HR_SALARY}
          label={t('laHRSalary')}
        />
        <RoleOption
          role={Roles.HR_PEOP}
          checked={empRoles.HR_PEOP}
          label={t('laHRPeople')}
        />
        <RoleOption
          role={Roles.HR_PEOP_DEV}
          checked={empRoles.HR_PEOP_DEV}
          label={t('laHRPeopleDev')}
        />
        <RoleOption
          role={Roles.RECRUITER}
          checked={empRoles.RECRUITER}
          label={t('laRecruiter')}
        />
      </Box>
      <FormHelperText>{helperText}</FormHelperText>
    </FormControl>
  );
};
